import React, { useEffect, useRef, useState } from 'react';
import api from '../api/api';
import { FaSpinner, FaArrowLeft, FaInfoCircle } from 'react-icons/fa';
import { useLocation } from 'react-router-dom';
import BrandPanel from './BrandPanel';
import { CURRENT_TICKER_KEY } from './v2/Header';

const CODE_LEN = 6;
const CODE_TTL_MS = 10 * 60 * 1000; // mirrors LoginController.SendLoginCode

const maskEmail = (email: string) => {
    const [ local, domain ] = email.split('@');
    return domain ? `${local[0]}•••••@${domain}` : email;
}

const LoginPage = () => {

    const [ username, setUsername ] = useState('');
    const [ password, setPassword ] = useState('');
    const [ showPassword, setShowPassword ] = useState(false);
    const [ code, setCode ] = useState('');
    const [ codeRequired, setCodeRequired ] = useState(false);
    const [ expiresAt, setExpiresAt ] = useState(0);
    const [ now, setNow ] = useState(Date.now());
    const [ resent, setResent ] = useState(false);
    const [ error, setError ] = useState('');
    const [ loading, setLoading ] = useState(false);
    const digitRefs = useRef<(HTMLInputElement | null)[]>([]);
    const location = useLocation();
    const { from } = (location.state as any) || { from: { pathname: "/" } };

    useEffect(() => {
        if (!codeRequired) return;
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, [codeRequired]);
    // inputs are disabled while loading, so focus only once that clears
    useEffect(() => { if (codeRequired && !loading) digitRefs.current[Math.min(code.length, CODE_LEN - 1)]?.focus(); }, [codeRequired, loading]);

    const login = (e?: React.FormEvent, withCode = codeRequired) =>{
        e?.preventDefault();
        setError('')
        setLoading(true)
        api().login(username, password, withCode ? code : undefined).then(result=>{
            // a fresh sign-in opens the profile's default client, not the last one used in this browser
            localStorage.removeItem(CURRENT_TICKER_KEY);
            window.location.href = from.pathname + (from.search || '');
        }).catch(err => {
            const msg = err?.response?.data;
            if (msg === 'CODE_REQUIRED') {
                setCodeRequired(true);
                setExpiresAt(Date.now() + CODE_TTL_MS);
            } else {
                setError(codeRequired ? msg || 'Invalid code' : 'Invalid username/password')
            }
            setLoading(false)
        });
    }

    // a login attempt without a code makes the server issue a fresh one
    const resend = () => { setResent(true); setCode(''); login(undefined, false); }
    const backToLogin = () => { setCodeRequired(false); setResent(false); setCode(''); setError(''); }

    const setDigits = (value: string, start: number) => {
        const digits = value.replace(/\D/g, '');
        if (!digits) return;
        // ponytail: code is always contiguous — typing into a later box appends
        const next = (code.slice(0, Math.min(start, code.length)) + digits).slice(0, CODE_LEN);
        setCode(next);
        digitRefs.current[Math.min(next.length, CODE_LEN - 1)]?.focus();
    }
    const onDigitKey = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace') {
            e.preventDefault();
            const at = code[i] ? i : i - 1;
            if (at < 0) return;
            setCode(code.slice(0, at));
            digitRefs.current[at]?.focus();
        }
    }

    const remainingMs = Math.max(0, expiresAt - now);
    const mm = Math.floor(remainingMs / 60000), ss = Math.floor(remainingMs / 1000) % 60;
    const countdown = remainingMs > 0 ? `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}` : 'Expired';

    return <div className="login-page">
        <BrandPanel />
        <div className="form-panel">
            <form className="login-box" onSubmit={login}>
                { !codeRequired && <>
                    <div className="heading">
                        <h2>Welcome back</h2>
                        <p>Sign in to your Investi workspace to continue.</p>
                    </div>
                    <div className="field">
                        <label htmlFor="login-username">Email address</label>
                        <div className="input-row">
                            <input id="login-username" type="text" autoComplete="username" onChange={(e: any) => setUsername(e.target.value)} disabled={loading} />
                        </div>
                    </div>
                    <div className="field">
                        <label htmlFor="login-password">Password</label>
                        <div className="input-row">
                            <input id="login-password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" onChange={(e: any) => setPassword(e.target.value)} disabled={loading} />
                            <button type="button" className="show-toggle" tabIndex={-1} onClick={() => setShowPassword(!showPassword)}>{showPassword ? 'Hide' : 'Show'}</button>
                        </div>
                    </div>
                </> }
                { codeRequired && <>
                    <button type="button" className="back-link" onClick={backToLogin} disabled={loading}><FaArrowLeft size={12} /> Back to sign in</button>
                    <div className="heading">
                        <h2>Check your email</h2>
                        <p>We sent a 6-digit verification code to <strong>{maskEmail(username)}</strong>. It expires in 10 minutes.</p>
                    </div>
                    <div className="field">
                        <div className="label-row">
                            <label htmlFor="login-code-0">Verification code</label>
                            <span className={'countdown' + (remainingMs ? '' : ' expired')}>{countdown}</span>
                        </div>
                        <div className="digits">
                            {Array.from({ length: CODE_LEN }, (_, i) => <React.Fragment key={i}>
                                {i === 3 && <span className="sep" />}
                                <input id={`login-code-${i}`} type="text" inputMode="numeric" autoComplete={i === 0 ? 'one-time-code' : 'off'} maxLength={i === 0 ? CODE_LEN : 1}
                                    value={code[i] || ''} disabled={loading}
                                    ref={el => { digitRefs.current[i] = el; }}
                                    onChange={e => setDigits(e.target.value, i)}
                                    onKeyDown={e => onDigitKey(i, e)}
                                    onPaste={e => { e.preventDefault(); setDigits(e.clipboardData.getData('text'), 0); }}
                                    onFocus={e => e.target.select()} />
                            </React.Fragment>)}
                        </div>
                    </div>
                </> }
                {error && <p className="error">{error}</p>}
                <button type="submit" disabled={loading || (codeRequired && code.length < CODE_LEN)}>{codeRequired ? 'Verify and Continue' : 'Sign In'} &nbsp;
                { loading && <FaSpinner className="fa-spin" size={16} color="#0b1739" />}
                </button>
                { codeRequired && <>
                    <div className="help">
                        <span>Didn't get the code?</span>
                        <button type="button" className="link" onClick={resend} disabled={resent || loading}>{resent ? 'Code resent' : 'Resend code'}</button>
                    </div>
                    <div className="callout">
                        <FaInfoCircle size={16} />
                        <p>Not arrived within a minute? Check your spam folder for an email from notifications@investi.com.au.</p>
                    </div>
                </> }
                { !codeRequired && <div className="help">
                    <span>Need access?</span> <a href="mailto:contact@investi.com.au">Contact support</a>
                </div> }
            </form>
        </div>
        </div>

}

export default LoginPage;
